import styled, { css } from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils';

/*
 * Wspólne klocki formularzy zamówień personelu (#365, #374): CreateOrder i CreateProductOrder.
 * Kolory z tokenów ds (bez gradientów i cieni), cele dotyku min. 44 px, koszyk w układzie strony
 * zamiast `position: fixed` nad paskiem aplikacji. Układ strony: StaffOrderLayout.styled.ts.
 */

/* ------------------------------------------------------------------- koszyk */

export const Cart = styled.section<{ $empty: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Telefon: zwinięty pasek przyklejony 8 px nad dolną krawędzią (i nad paskiem okresu próbnego),
     żeby koszyk był pod ręką, ale nie spychał katalogu. Tylko tu ma tło i ramkę, bo leży nad treścią.
     Pusty koszyk na telefonie się nie pokazuje. */
  ${media.mobile} {
    position: sticky;
    bottom: calc(var(--hs-trial-bar-height, 0px) + env(safe-area-inset-bottom, 0px) + 8px);
    /* Nad polami HeroUI (etykiety mają z-index 10). */
    z-index: 20;
    padding: 8px 12px;
    gap: 4px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
    ${({ $empty }) => $empty && css`
      display: none;
    `}
  }
`;

export const CartEmpty = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CartHeader = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  cursor: default;

  ${media.mobile} {
    cursor: pointer;
    font-size: 15px;
  }
`;

export const CartHeaderRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const CartTotalInline = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${ds.accentInk};

  ${({ theme }) => theme.mode === 'dark' && css`
    color: ${theme.colors.text.primary};
  `}
`;

export const CartToggleHint = styled.span`
  display: none;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${media.mobile} {
    display: inline;
  }
`;

export const CartItems = styled.ul<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 420px;
  overflow-y: auto;

  ${media.mobile} {
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
    max-height: 45vh;
  }
`;

export const CartItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border-radius: 8px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : ds.bgMuted)};
`;

export const CartItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const CartItemBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Qty = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/** Przyciski ilości: 40 px (wcześniej ok. 28×22 px), czytelne na telefonie kelnera. */
export const QtyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : ds.borderDefault)};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${ds.accentInk};
  }

  &:focus-visible {
    outline: 2px solid ${ds.accentInk};
    outline-offset: 2px;
  }
`;

export const QtyValue = styled.span`
  min-width: 2ch;
  text-align: center;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RemoveButton = styled.button`
  flex-shrink: 0;
  min-height: 32px;
  padding: 0 4px;
  border: 0;
  background: none;
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fca5a5' : '#b91c1c')};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

export const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};

  strong {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  ${media.mobile} {
    display: none;
  }
`;

/* ------------------------------------------------------------- produkty */

export const CategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** Nagłówek kategorii: h4 pod tytułem sekcji (h3 w FormSection), lżejszy, ale czytelny. */
export const CategoryTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ProductGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${media.mobile} {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

export const ProductCard = styled.li<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid ${({ $selected, theme }) => {
    if ($selected) return ds.accentInk;
    return theme.mode === 'dark' ? '#374151' : ds.borderDefault;
  }};
  background: ${({ $selected, theme }) => {
    if (theme.mode === 'dark') return $selected ? 'rgba(255, 255, 255, 0.04)' : theme.colors.background.card;
    return $selected ? ds.bgMuted : '#ffffff';
  }};
  ${({ $disabled }) => $disabled && css`
    opacity: 0.6;
  `}
`;

/** Miniatura 56 px w wierszu (zamiast obrazu 260×140 na całą kartę). */
export const Thumb = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : ds.bgMuted)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProductBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const ProductName = styled.span`
  font-size: 15px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const ProductDescription = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ProductFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
`;

export const ProductPrice = styled.span`
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

export const AddButton = styled.button`
  min-height: 40px;
  white-space: nowrap;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid ${ds.accentInk};
  background: ${ds.accentInk};
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${ds.accentInkHover};
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${ds.borderDefault};
    background: ${ds.bgMuted};
    color: ${ds.textMuted};
  }

  &:focus-visible {
    outline: 2px solid ${ds.accentInk};
    outline-offset: 2px;
  }
`;

export const ProductUnavailable = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
